import { useEffect, useState } from 'react';
import { Store } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 「显示发货信息」开关 —— 一个列表一个,不是一张卡一个。
 *
 * ★ 为什么做成列表级:
 *
 *   绝大多数单是从同一个仓库发的,所以默认收起来一屏能多看两三单 ——
 *   而这一屏的用途正是「扫一遍今天有什么、哪几单顺路」。
 *
 *   真要看发货方的时候,司机的问题从来不是「这一单从哪拿」,
 *   而是「**这几单是不是同一个地方拿**」—— 那需要同时看见好几单。
 *   做成一张卡一个开关的话,他得一张张点开再对比,
 *   而且每张卡上都挂着一个按钮,把这一屏搞得很吵。
 *
 * ★ 记在 localStorage 里。
 *   多取货点的车队每天都要看发货方,让他每次打开 App 都重点一次
 *   是纯粹的重复劳动;而单仓库的车队点一次关掉之后就不用再管。
 */

const KEY = 'delivery.driver.showPickup';

export function usePickupVisible(): [boolean, (v: boolean) => void] {
  const [on, setOn] = useState(() => {
    try {
      return localStorage.getItem(KEY) === '1';
    } catch {
      // 隐私模式下读不到 localStorage —— 那就用默认值,不该因此白屏
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, on ? '1' : '0');
    } catch {
      /* 存不下就算了,只是下次打开要重新点一下 */
    }
  }, [on]);

  return [on, setOn];
}

export function PickupToggle({
  on,
  onChange,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="显示发货信息"
      onClick={() => onChange(!on)}
      className="flex items-center gap-1.5 rounded-full border border-slate-300 px-2.5 py-1
                 text-xs text-slate-600 dark:border-slate-600 dark:text-slate-300"
    >
      <InlineIcon icon={Store} />
      {on ? '隐藏发货信息' : '显示发货信息'}
    </button>
  );
}
